/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { useCallback, useEffect, useMemo } from 'react';
import { useFormDraft } from '@/shared/hooks/use-form-draft';
import { useDraftUserId } from '@/shared/hooks/use-draft-user-id';

export interface QuickClientDraftValues extends Record<string, unknown> {
  fullName: string;
  phone: string;
  email: string;
}

const EMPTY: QuickClientDraftValues = { fullName: '', phone: '', email: '' };
/** Third-party contact data (LGPD): keep it for one day, not seven. */
const QUICK_CLIENT_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Local draft of the quick client form (name, phone, e-mail of a third party).
 *
 * @param agencyId - The owning agency.
 * @param values - The live field values.
 * @param apply - Applies restored values to the fields.
 * @returns The offer and the restore/discard/clear/keep actions.
 */
export function useQuickClientDraft(
  agencyId: string,
  values: QuickClientDraftValues,
  apply: (restored: QuickClientDraftValues) => void,
) {
  const userId = useDraftUserId();
  const { fullName, phone, email } = values;
  const current = useMemo(() => ({ fullName, phone, email }), [fullName, phone, email]);
  const draft = useFormDraft<QuickClientDraftValues>({
    scope: { userId, agencyId, kind: 'quick-client' },
    initial: EMPTY,
    active: true,
    ttlMs: QUICK_CLIENT_TTL_MS,
    getCurrent: () => current,
  });
  const { schedule, flush, restore: take } = draft;

  useEffect(() => { schedule(current); }, [current, schedule]);

  const restore = useCallback(() => {
    const data = take();
    if (data) apply({ ...EMPTY, ...data });
  }, [take, apply]);

  const keep = useCallback(() => flush(current), [flush, current]);

  return { offer: draft.offer, restore, discard: draft.discard, clear: draft.clear, keep };
}
